import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Topic Editor Component
 *
 * Full-page editor for creating and editing topics
 * Following PlaybookEditor pattern with two-column layout
 *
 * @package WooAIChatbot
 */
import { useState, useEffect } from 'react';
import { ArrowLeft, Save, Loader2, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@components/ui/card';
import apiClient from '../../../utils/api/client';
import { useAsync } from '../../../utils/hooks/useAsync';
const DETECTION_METHODS = [
    { value: 'keyword', label: 'Keyword Matching', description: 'Match exact keywords in user messages' },
    { value: 'intent', label: 'Intent Detection', description: 'Detect user intent using AI' },
    { value: 'context', label: 'Context Analysis', description: 'Analyze conversation context' },
    { value: 'ml', label: 'Machine Learning', description: 'Use ML models for classification' },
];
const EMOJI_OPTIONS = ['📦', '🛒', '📋', '💬', '🔧', '📊', '🎯', '💡', '🔍', '📝', '⚙️', '🌟', '🎁', '📞', '💳'];
/**
 * Topic Editor Component
 */
export function TopicEditor({ topicId, onBack, onSave }) {
    const isEditMode = !!topicId;
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        icon: '📦',
        keywords: [],
        patterns: [],
        priority: 5,
        enabled: true,
        detection_methods: ['keyword'],
    });
    const [keywordInput, setKeywordInput] = useState('');
    const [patternInput, setPatternInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    // Load topic data when editing
    const { data: topic, loading, error: loadError, } = useAsync(() => (topicId ? apiClient.get(`topics/${topicId}`) : Promise.resolve(null)), [topicId], !!topicId);
    // Populate form when topic loads
    useEffect(() => {
        if (topic) {
            setFormData({
                name: topic.name || '',
                slug: topic.slug || '',
                description: topic.description || '',
                icon: topic.icon || '📦',
                keywords: topic.keywords || [],
                patterns: topic.patterns || [],
                priority: topic.priority || 5,
                enabled: topic.enabled !== false,
                detection_methods: topic.detection_methods || ['keyword'],
            });
        }
    }, [topic]);
    /**
     * Handle input change
     */
    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };
    /**
     * Generate slug from name
     */
    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_|_$/g, '');
    };
    /**
     * Handle name change with auto-slug
     */
    const handleNameChange = (name) => {
        setFormData((prev) => ({
            ...prev,
            name,
            ...((!isEditMode && !prev.slug) ? { slug: generateSlug(name) } : {}),
        }));
    };
    /**
     * Add keyword
     */
    const handleAddKeyword = () => {
        if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
            handleChange('keywords', [...formData.keywords, keywordInput.trim()]);
            setKeywordInput('');
        }
    };
    /**
     * Remove keyword
     */
    const handleRemoveKeyword = (keyword) => {
        handleChange('keywords', formData.keywords.filter((k) => k !== keyword));
    };
    /**
     * Add pattern
     */
    const handleAddPattern = () => {
        if (patternInput.trim() && !formData.patterns.includes(patternInput.trim())) {
            handleChange('patterns', [...formData.patterns, patternInput.trim()]);
            setPatternInput('');
        }
    };
    /**
     * Remove pattern
     */
    const handleRemovePattern = (pattern) => {
        handleChange('patterns', formData.patterns.filter((p) => p !== pattern));
    };
    /**
     * Toggle detection method
     */
    const handleToggleMethod = (method) => {
        const methods = formData.detection_methods.includes(method)
            ? formData.detection_methods.filter((m) => m !== method)
            : [...formData.detection_methods, method];
        handleChange('detection_methods', methods);
    };
    /**
     * Save topic
     */
    const handleSave = async () => {
        if (!formData.name.trim()) {
            setError('Topic name is required');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...formData,
                slug: formData.slug || generateSlug(formData.name),
            };
            if (isEditMode) {
                await apiClient.put(`topics/${topicId}`, payload);
            }
            else {
                await apiClient.post('topics', payload);
            }
            onSave?.();
            onBack();
        }
        catch (err) {
            console.error('Failed to save topic:', err);
            setError(`Failed to ${isEditMode ? 'update' : 'create'} topic. Please try again.`);
        }
        finally {
            setSaving(false);
        }
    };
    // Loading state
    if (loading) {
        return (_jsx("div", { style: { padding: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }, children: _jsx(Loader2, { style: { width: '32px', height: '32px', animation: 'spin 1s linear infinite' } }) }));
    }
    // Error state
    if (loadError) {
        return (_jsx("div", { style: { padding: '32px' }, children: _jsxs("div", { style: { padding: '24px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #ef4444' }, children: [_jsxs("p", { style: { color: '#ef4444' }, children: ["Error loading topic: ", loadError.message] }), _jsx("button", { onClick: onBack, style: {
                            marginTop: '16px',
                            height: '36px',
                            padding: '0 16px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            background: 'white',
                            cursor: 'pointer',
                        }, children: "Go Back" })] }) }));
    }
    return (_jsxs("div", { style: { padding: '32px 48px', display: 'flex', flexDirection: 'column', gap: '24px' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }, children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '16px' }, children: [_jsx("button", { onClick: onBack, style: {
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    border: '1px solid #d1d5db',
                                    background: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }, children: _jsx(ArrowLeft, { style: { width: '20px', height: '20px', color: '#6b7280' } }) }), _jsxs("div", { children: [_jsx("h1", { style: { fontSize: '24px', fontWeight: 500, color: '#111827', margin: 0 }, children: isEditMode ? 'Edit Topic' : 'Create Topic' }), _jsx("p", { style: { fontSize: '14px', color: '#6b7280', margin: 0 }, children: isEditMode ? 'Modify topic settings and detection rules' : 'Create a new conversation topic' })] })] }), _jsx("button", { onClick: handleSave, disabled: saving || !formData.name.trim(), style: {
                            height: '40px',
                            padding: '0 24px',
                            borderRadius: '10px',
                            border: 'none',
                            background: saving || !formData.name.trim() ? '#d1d5db' : '#6366f1',
                            color: 'white',
                            fontSize: '16px',
                            cursor: saving || !formData.name.trim() ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }, children: saving ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { style: { width: '16px', height: '16px', animation: 'spin 1s linear infinite' } }), "Saving..."] })) : (_jsxs(_Fragment, { children: [_jsx(Save, { style: { width: '16px', height: '16px' } }), isEditMode ? 'Save Changes' : 'Create Topic'] })) })] }), error && (_jsx("div", { style: { padding: '12px 16px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #ef4444', color: '#ef4444', fontSize: '14px' }, children: error })), _jsxs("div", { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }, children: [_jsxs(Card, { style: { borderRadius: '14px', border: '1px solid rgba(0,0,0,0.1)' }, children: [_jsxs(CardHeader, { children: [_jsx(CardTitle, { style: { fontSize: '18px', fontWeight: 500 }, children: "Basic Information" }), _jsx(CardDescription, { children: "Topic name, description, and icon" })] }), _jsxs(CardContent, { style: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsxs("div", { children: [_jsx("label", { style: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }, children: "Icon" }), _jsxs("div", { style: { position: 'relative' }, children: [_jsx("button", { type: "button", onClick: () => setShowEmojiPicker(!showEmojiPicker), style: {
                                                            width: '60px',
                                                            height: '60px',
                                                            fontSize: '32px',
                                                            borderRadius: '12px',
                                                            border: '1px solid #d1d5db',
                                                            background: 'white',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }, children: formData.icon }), showEmojiPicker && (_jsx("div", { style: {
                                                            position: 'absolute',
                                                            top: '70px',
                                                            left: 0,
                                                            background: 'white',
                                                            border: '1px solid #d1d5db',
                                                            borderRadius: '12px',
                                                            padding: '12px',
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(5, 1fr)',
                                                            gap: '8px',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                            zIndex: 10,
                                                        }, children: EMOJI_OPTIONS.map((emoji) => (_jsx("button", { type: "button", onClick: () => {
                                                                handleChange('icon', emoji);
                                                                setShowEmojiPicker(false);
                                                            }, style: {
                                                                width: '40px',
                                                                height: '40px',
                                                                fontSize: '24px',
                                                                border: formData.icon === emoji ? '2px solid #6366f1' : '1px solid #e5e7eb',
                                                                borderRadius: '8px',
                                                                background: 'white',
                                                                cursor: 'pointer',
                                                            }, children: emoji }, emoji))) }))] })] }), _jsxs("div", { children: [_jsx("label", { style: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }, children: "Topic Name *" }), _jsx("input", { type: "text", value: formData.name, onChange: (e) => handleNameChange(e.target.value), placeholder: "e.g., Product Information", style: {
                                                    width: '100%',
                                                    height: '40px',
                                                    padding: '0 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #d1d5db',
                                                    fontSize: '14px',
                                                    boxSizing: 'border-box',
                                                } })] }), _jsxs("div", { children: [_jsx("label", { style: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }, children: "Slug" }), _jsx("input", { type: "text", value: formData.slug, onChange: (e) => handleChange('slug', e.target.value), placeholder: "product_information", disabled: isEditMode, style: {
                                                    width: '100%',
                                                    height: '40px',
                                                    padding: '0 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #d1d5db',
                                                    fontSize: '14px',
                                                    boxSizing: 'border-box',
                                                    background: isEditMode ? '#f3f4f6' : 'white',
                                                    cursor: isEditMode ? 'not-allowed' : 'text',
                                                } }), _jsx("p", { style: { marginTop: '4px', fontSize: '12px', color: '#6b7280' }, children: isEditMode ? 'Slug cannot be changed after creation' : 'Auto-generated from name if left empty' })] }), _jsxs("div", { children: [_jsx("label", { style: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }, children: "Description" }), _jsx("textarea", { value: formData.description, onChange: (e) => handleChange('description', e.target.value), placeholder: "Describe what this topic covers...", rows: 4, style: {
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #d1d5db',
                                                    fontSize: '14px',
                                                    resize: 'vertical',
                                                    boxSizing: 'border-box',
                                                } })] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }, children: [_jsxs("div", { children: [_jsx("span", { style: { fontSize: '14px', fontWeight: 500, color: '#374151' }, children: "Enabled" }), _jsx("p", { style: { fontSize: '12px', color: '#6b7280', margin: 0 }, children: "Make this topic active" })] }), _jsx("button", { type: "button", onClick: () => handleChange('enabled', !formData.enabled), style: {
                                                    width: '44px',
                                                    height: '24px',
                                                    borderRadius: '12px',
                                                    border: 'none',
                                                    background: formData.enabled ? '#6366f1' : '#d1d5db',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    transition: 'background 0.2s',
                                                }, children: _jsx("span", { style: {
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: formData.enabled ? '22px' : '2px',
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        background: 'white',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                        transition: 'left 0.2s',
                                                    } }) })] })] })] }), _jsxs(Card, { style: { borderRadius: '14px', border: '1px solid rgba(0,0,0,0.1)' }, children: [_jsxs(CardHeader, { children: [_jsx(CardTitle, { style: { fontSize: '18px', fontWeight: 500 }, children: "Detection Settings" }), _jsx(CardDescription, { children: "Configure how this topic is detected" })] }), _jsxs(CardContent, { style: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: [_jsxs("div", { children: [_jsxs("label", { style: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }, children: ["Priority: ", formData.priority] }), _jsx("input", { type: "range", min: "1", max: "10", value: formData.priority, onChange: (e) => handleChange('priority', parseInt(e.target.value)), style: {
                                                    width: '100%',
                                                    accentColor: '#6366f1',
                                                } }), _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }, children: [_jsx("span", { children: "Low (1)" }), _jsx("span", { children: "High (10)" })] })] }), _jsxs("div", { children: [_jsx("label", { style: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }, children: "Detection Methods" }), _jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '8px' }, children: DETECTION_METHODS.map((method) => (_jsxs("button", { type: "button", onClick: () => handleToggleMethod(method.value), style: {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        padding: '12px',
                                                        borderRadius: '8px',
                                                        border: formData.detection_methods.includes(method.value)
                                                            ? '2px solid #6366f1'
                                                            : '1px solid #e5e7eb',
                                                        background: formData.detection_methods.includes(method.value)
                                                            ? '#eef2ff'
                                                            : 'white',
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                    }, children: [_jsx("div", { style: {
                                                                width: '20px',
                                                                height: '20px',
                                                                borderRadius: '4px',
                                                                border: formData.detection_methods.includes(method.value)
                                                                    ? '2px solid #6366f1'
                                                                    : '2px solid #d1d5db',
                                                                background: formData.detection_methods.includes(method.value)
                                                                    ? '#6366f1'
                                                                    : 'white',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'white',
                                                                fontSize: '14px',
                                                            }, children: formData.detection_methods.includes(method.value) && '✓' }), _jsxs("div", { children: [_jsx("div", { style: { fontSize: '14px', fontWeight: 500, color: '#374151' }, children: method.label }), _jsx("div", { style: { fontSize: '12px', color: '#6b7280' }, children: method.description })] })] }, method.value))) })] }), _jsxs("div", { children: [_jsx("label", { style: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }, children: "Keywords" }), _jsxs("div", { style: { display: 'flex', gap: '8px' }, children: [_jsx("input", { type: "text", value: keywordInput, onChange: (e) => setKeywordInput(e.target.value), onKeyPress: (e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword()), placeholder: "Add keyword...", style: {
                                                            flex: 1,
                                                            height: '40px',
                                                            padding: '0 12px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #d1d5db',
                                                            fontSize: '14px',
                                                            boxSizing: 'border-box',
                                                        } }), _jsxs("button", { type: "button", onClick: handleAddKeyword, style: {
                                                            height: '40px',
                                                            padding: '0 16px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #d1d5db',
                                                            background: 'white',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                        }, children: [_jsx(Plus, { style: { width: '16px', height: '16px' } }), "Add"] })] }), formData.keywords.length > 0 && (_jsx("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }, children: formData.keywords.map((keyword) => (_jsxs("span", { style: {
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        padding: '4px 10px',
                                                        borderRadius: '16px',
                                                        background: '#eef2ff',
                                                        color: '#6366f1',
                                                        fontSize: '12px',
                                                    }, children: [keyword, _jsx("button", { type: "button", onClick: () => handleRemoveKeyword(keyword), style: {
                                                                border: 'none',
                                                                background: 'transparent',
                                                                cursor: 'pointer',
                                                                padding: 0,
                                                                lineHeight: 1,
                                                                color: '#6366f1',
                                                            }, children: _jsx(X, { style: { width: '12px', height: '12px' } }) })] }, keyword))) }))] }), _jsxs("div", { children: [_jsx("label", { style: { display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }, children: "Regex Patterns (Advanced)" }), _jsxs("div", { style: { display: 'flex', gap: '8px' }, children: [_jsx("input", { type: "text", value: patternInput, onChange: (e) => setPatternInput(e.target.value), onKeyPress: (e) => e.key === 'Enter' && (e.preventDefault(), handleAddPattern()), placeholder: "Add regex pattern...", style: {
                                                            flex: 1,
                                                            height: '40px',
                                                            padding: '0 12px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #d1d5db',
                                                            fontSize: '14px',
                                                            fontFamily: 'monospace',
                                                            boxSizing: 'border-box',
                                                        } }), _jsxs("button", { type: "button", onClick: handleAddPattern, style: {
                                                            height: '40px',
                                                            padding: '0 16px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #d1d5db',
                                                            background: 'white',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                        }, children: [_jsx(Plus, { style: { width: '16px', height: '16px' } }), "Add"] })] }), formData.patterns.length > 0 && (_jsx("div", { style: { display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }, children: formData.patterns.map((pattern) => (_jsxs("div", { style: {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '8px 12px',
                                                        borderRadius: '6px',
                                                        background: '#f3f4f6',
                                                        fontFamily: 'monospace',
                                                        fontSize: '12px',
                                                    }, children: [_jsx("code", { children: pattern }), _jsx("button", { type: "button", onClick: () => handleRemovePattern(pattern), style: {
                                                                border: 'none',
                                                                background: 'transparent',
                                                                cursor: 'pointer',
                                                                padding: 0,
                                                                color: '#6b7280',
                                                            }, children: _jsx(X, { style: { width: '14px', height: '14px' } }) })] }, pattern))) })), _jsx("p", { style: { marginTop: '4px', fontSize: '12px', color: '#6b7280' }, children: "Use JavaScript regex syntax. Patterns will be used for advanced topic detection." })] })] })] })] })] }));
}
